import {
  SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  buildSheetChunkId,
  buildSheetChunkKey,
  buildSpreadsheetStyleFromFormat,
  type SpreadsheetCellRecord,
  type SpreadsheetCellValue,
  type SpreadsheetWorkbookSnapshot,
} from '../../../protocol/spreadsheetEngineProtocol'
import {
  GL_DETAIL_NUMERIC_COLUMNS,
  glDetailColumnLabel,
  parseNumberish,
} from '../quickBooksGlDetailShared'
import { EXCEL_WORKSHEET_ROW_LIMIT, formatGlCell, type CfoGlDetailTotals } from './cfoGlDetailQuery'

export type CfoGlDetailExcelBuildArgs = {
  companyName?: string | null
  rows: Record<string, any>[]
  columns: string[]
  totals: CfoGlDetailTotals
  filtersLabel?: string | null
  splitAcrossSheets?: boolean
}

type GlExcelCell = string | number | boolean | null | undefined

const safeExcelSegment = (raw: string): string => (
  String(raw || '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90)
)

export const CFO_GL_DETAIL_EXCEL_META_ROWS = 5
export const CFO_GL_DETAIL_EXCEL_DATA_ROWS_PER_SHEET = EXCEL_WORKSHEET_ROW_LIMIT - CFO_GL_DETAIL_EXCEL_META_ROWS

export function buildCfoGlDetailExcelFileName(companyName?: string | null): string {
  const company = safeExcelSegment(companyName || '')
  const base = safeExcelSegment(`CFO Core GL Detail${company ? ` ${company}` : ''} ${new Date().toISOString().slice(0, 10)}`)
  return `${base || 'CFO Core GL Detail'}.xlsx`
}

export function buildCfoGlDetailCsvFileName(companyName?: string | null): string {
  const company = safeExcelSegment(companyName || '')
  const base = safeExcelSegment(`CFO Core GL Detail${company ? ` ${company}` : ''} ${new Date().toISOString().slice(0, 10)}`)
  return `${base || 'CFO Core GL Detail'}.csv`
}

export function requiredCfoGlDetailExcelSheetCount(rowCount: number): number {
  const rows = Math.max(0, Math.floor(Number(rowCount) || 0))
  return Math.max(1, Math.ceil(rows / CFO_GL_DETAIL_EXCEL_DATA_ROWS_PER_SHEET))
}

function excelCellValue(value: GlExcelCell): SpreadsheetCellValue {
  if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'number', value }
  if (typeof value === 'boolean') return { kind: 'boolean', value }
  if (typeof value === 'string') return { kind: 'text', value }
  return { kind: 'blank' }
}

function excelRawInput(value: GlExcelCell): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'string') return value
  return ''
}

function excelCell(value: GlExcelCell, styleId?: string | null): SpreadsheetCellRecord {
  return {
    raw_input: excelRawInput(value),
    computed_value: excelCellValue(value),
    style_id: styleId || null,
    revision: 1,
  }
}

export function buildCfoGlDetailExcelSnapshot(args: CfoGlDetailExcelBuildArgs): SpreadsheetWorkbookSnapshot {
  if (!args.splitAcrossSheets && args.rows.length + CFO_GL_DETAIL_EXCEL_META_ROWS > EXCEL_WORKSHEET_ROW_LIMIT) {
    throw new Error(`Excel export is limited to ${EXCEL_WORKSHEET_ROW_LIMIT.toLocaleString()} worksheet rows. Narrow the GL filters before exporting.`)
  }
  const columns = args.columns.length ? args.columns : ['txn_date']
  const title = args.companyName ? `${args.companyName} GL detail` : 'GL detail'
  const meta = args.filtersLabel || `Exported ${new Date().toLocaleString()}`
  const styles: Record<string, Record<string, unknown>> = {}
  const addStyle = (id: string, format: Record<string, unknown>): string => {
    const built = buildSpreadsheetStyleFromFormat(format)
    if (built.style) styles[id] = built.style
    return id
  }
  addStyle('gl_title', { bold: true, fontSize: 15, textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle('gl_meta', { fontSize: 10, textColor: '#64748B', align: 'left', verticalAlign: 'middle' })
  addStyle('gl_total_label', { bold: true, textColor: '#1F2937', backgroundColor: '#ECFEFF', align: 'left', verticalAlign: 'middle' })
  addStyle('gl_total_number', { bold: true, numberFormat: { formatCode: '#,##0.00;(#,##0.00);-' }, backgroundColor: '#ECFEFF', align: 'right', verticalAlign: 'middle' })
  addStyle('gl_header', { bold: true, textColor: '#334155', backgroundColor: '#F8FAFC', align: 'left', verticalAlign: 'middle', border: { bottom: { style: 'thin', color: '#CBD5E1' } } })
  addStyle('gl_text', { textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle('gl_number', { numberFormat: { formatCode: '#,##0.00;(#,##0.00);-' }, align: 'right', verticalAlign: 'middle' })
  addStyle('gl_date', { numberFormat: { formatCode: 'yyyy-mm-dd' }, align: 'left', verticalAlign: 'middle' })

  const colCount = Math.max(1, columns.length)
  const rowTotalLabel = `Rows: ${args.totals.rowCount.toLocaleString()}${args.totals.minDate || args.totals.maxDate ? ` | Dates: ${args.totals.minDate || '...'} to ${args.totals.maxDate || '...'}` : ''}`
  const dataRowsPerSheet = args.splitAcrossSheets ? CFO_GL_DETAIL_EXCEL_DATA_ROWS_PER_SHEET : args.rows.length
  const sheetCount = args.splitAcrossSheets ? requiredCfoGlDetailExcelSheetCount(args.rows.length) : 1
  const sheets: SpreadsheetWorkbookSnapshot['sheets'] = []
  const sheetOrder: string[] = []
  for (let sheetIndex = 0; sheetIndex < sheetCount; sheetIndex += 1) {
    const sheetId = `sheet_${sheetIndex + 1}`
    const start = sheetIndex * dataRowsPerSheet
    const sheetRows = args.rows.slice(start, start + dataRowsPerSheet)
    const sheetRangeLabel = args.splitAcrossSheets && sheetCount > 1
      ? `Sheet rows ${(start + 1).toLocaleString()}-${(start + sheetRows.length).toLocaleString()} of ${args.rows.length.toLocaleString()}`
      : rowTotalLabel
    const totalsRow = columns.map((column, index) => {
      if (index === 0) return sheetIndex === 0 ? 'Filtered totals' : 'Totals on sheet 1'
      if (sheetIndex === 0 && GL_DETAIL_NUMERIC_COLUMNS.has(column)) return Number(args.totals.totals[column] || 0)
      return ''
    })
    const matrix: GlExcelCell[][] = [
      [sheetIndex === 0 ? title : `${title} (${sheetIndex + 1})`],
      [meta],
      [sheetRangeLabel],
      totalsRow,
      columns.map(glDetailColumnLabel),
      ...sheetRows.map((row) => columns.map((column) => (
        GL_DETAIL_NUMERIC_COLUMNS.has(column)
          ? parseNumberish(row[column])
          : formatGlCell(row[column], column)
      ))),
    ]
    const chunks: Record<string, Record<string, SpreadsheetCellRecord>> = {}
    matrix.forEach((rowValues, rowIndex) => {
      rowValues.forEach((value, colIndex) => {
        if (value == null || value === '') return
        const chunkId = buildSheetChunkId(buildSheetChunkKey(sheetId, rowIndex, colIndex))
        chunks[chunkId] ??= {}
        const column = columns[colIndex] || ''
        const styleId = rowIndex === 0
          ? 'gl_title'
          : rowIndex === 1 || rowIndex === 2
            ? 'gl_meta'
            : rowIndex === 3
              ? GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'gl_total_number' : 'gl_total_label'
              : rowIndex === 4
                ? 'gl_header'
                : GL_DETAIL_NUMERIC_COLUMNS.has(column)
                  ? 'gl_number'
                  : column.includes('date')
                    ? 'gl_date'
                    : 'gl_text'
        chunks[chunkId][`${rowIndex}:${colIndex}`] = excelCell(value, styleId)
      })
    })
    const rowCount = Math.max(6, matrix.length)
    sheetOrder.push(sheetId)
    sheets.push({
      sheet_id: sheetId,
      name: sheetIndex === 0 ? 'GL Detail' : `GL Detail ${sheetIndex + 1}`,
      row_count: rowCount,
      col_count: colCount,
      chunks,
      column_metadata: Object.fromEntries(columns.map((column, index) => [
        String(index),
        { width_px: GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 112 : Math.min(240, Math.max(96, glDetailColumnLabel(column).length * 10 + 42)) },
      ])),
      frozen_panes: { rows: CFO_GL_DETAIL_EXCEL_META_ROWS, cols: 0 },
      merge_ranges: {
        title: { sheet_id: sheetId, start_row: 0, start_col: 0, end_row: 0, end_col: colCount - 1 },
        meta: { sheet_id: sheetId, start_row: 1, start_col: 0, end_row: 1, end_col: colCount - 1 },
        row_count: { sheet_id: sheetId, start_row: 2, start_col: 0, end_row: 2, end_col: colCount - 1 },
      },
      auto_filter: {
        range: { sheet_id: sheetId, start_row: 4, start_col: 0, end_row: Math.max(4, rowCount - 1), end_col: colCount - 1 },
      },
    })
  }

  return {
    protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    workbook_id: `cfo_core_gl_detail_${Date.now().toString(36)}`,
    workbook_revision: 1,
    workbook_properties: { date_1904: false },
    document_properties: {
      title,
      creator: 'FuseGrid CFO Core',
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
    sheets,
    sheet_order: sheetOrder,
    named_ranges: {},
    validation_rules: {},
    styles,
    conditional_formats: {},
    sparkline_groups: {},
    derived_artifacts: {},
    external_sources: {},
  }
}
